/* eslint-disable react/no-unescaped-entities */
import DocPageLayout, { Section } from "@/components/DocPageLayout";

// Step list helper — keeps each numbered flow visually consistent.
function Steps({ steps }) {
  return (
    <ol className="space-y-3 mt-2">
      {steps.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="shrink-0 w-6 h-6 flex items-center justify-center font-mono text-[10px] font-bold border border-[#FF5F1F] text-[#FF5F1F]">
            {i + 1}
          </span>
          <span className="text-[14px] leading-relaxed text-zinc-300">{s}</span>
        </li>
      ))}
    </ol>
  );
}

export default function HowTo() {
  return (
    <DocPageLayout
      subtitle="Playbook"
      title="How To Hunt57"
      intro="Every user flow, spelled out step-by-step. If you've never touched a card platform before, this is your onboarding manual. If you're a veteran, use it to make sure our conventions match yours."
    >
      <Section title="Create Your Account" id="signup">
        <Steps steps={[
          "Go to /register — the button is in the top-right of any public page.",
          "Pick a username (3-24 chars, letters/numbers/underscore/dash). This becomes your public profile URL: /u/YOUR_USERNAME.",
          "Enter your email and a password (6+ characters).",
          "Read the Hunt57 Terms in the acknowledgment box and check the confirmation box. You cannot register without it — that's on purpose.",
          "Click 'Create Free Account'. You're immediately logged in and land on Browse.",
        ]} />
        <p><strong className="text-white">What you can do with a free account:</strong> browse every listing, save cards to your Watchlist, message any seller, open Purchase Orders, leave feedback, and follow the community. You cannot list cards for sale until you upgrade to a vendor account.</p>
      </Section>

      <Section title="Browse & Search Cards" id="browse">
        <p>The Browse page is your primary discovery surface at <span className="font-mono text-[#FF5F1F]">/browse</span>.</p>
        <p><strong className="text-white">Featured strip:</strong> Top Dog (highest-priced active card), Just Listed (15 most recent, auto-scrolling), Fan Favorites (top 8 most liked). The Pack column shows community-wide counts.</p>
        <p><strong className="text-white">Sidebar filters:</strong> Player/Keyword, Seller (@username, with typeahead suggestions), Sport, Card Type (Base/Insert/Variation/Rookie/Auto/Relic/Numbered/SSP/SP/Graded), Team+Brand, Year, Price range, Accepts Offers.</p>
        <p><strong className="text-white">Sort:</strong> Newest, Price Low→High, Price High→Low. Sort chips are above the grid.</p>
        <p><strong className="text-white">Card tiles show:</strong> the front image, title, seller username, price, like count, and small chips for Combined Shipping (blue), Autograph (pen), Graded (badge), and Offers/Firm (green/red). On mobile the Combo and Offers labels collapse to icons only.</p>
      </Section>

      <Section title="View a Listing in Detail" id="listing-detail">
        <p>Click any card in the grid to open <span className="font-mono text-[#FF5F1F]">/listing/:id</span>. From this page you can:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>View full front + back photos (click to zoom).</li>
          <li>Read notes, card details (year, brand, set, card #, parallel, grade, cert #), and shipping info.</li>
          <li>See combined-shipping policy inline if the seller offers it.</li>
          <li>Purchase, Message the seller, Watch (add to private watchlist), or Like (public 👍 counter).</li>
          <li>Use <strong>Prev / Next</strong> buttons (or ← / → keyboard arrows) to step through the grid you came from without going back.</li>
        </ul>
      </Section>

      <Section title="Watchlist a Card" id="watchlist">
        <p>Your Watchlist is a private "keep an eye on this" list — the seller never sees who has watched their card.</p>
        <Steps steps={[
          "Open any listing detail page.",
          "Click the heart-icon Watch button.",
          "The card appears in /watchlist. Click again to remove.",
          "Prev/Next navigation works inside the Watchlist too.",
        ]} />
      </Section>

      <Section title="Like a Card" id="like">
        <p>Likes are <strong className="text-white">public</strong>. Every card shows a 👍 counter and each user gets one like per listing. The 8 most-liked active cards feed the Fan Favorites section on Browse.</p>
        <p>Click the thumbs-up chip on any tile or the price row of the detail page. The counter updates instantly.</p>
      </Section>

      <Section title="Message a Seller" id="messages">
        <Steps steps={[
          "Open the listing detail page and click 'Message @seller'.",
          "A modal opens with the card title pre-filled in context. If the listing is FIRM, you'll see a yellow warning — offers here can strike your account.",
          "Type your message and Send. You're taken to the conversation thread at /messages/:convoId.",
          "All future messages appear in /messages (Inbox / Archived tabs).",
          "To report an incoming message, hover it and click the flag icon.",
        ]} />
      </Section>

      <Section title="Open a Purchase Order (Buyer)" id="po-buyer">
        <p>The PO flow is Hunt57's transaction spine. It's a paper trail — Hunt57 never holds your money.</p>
        <Steps steps={[
          "Click 'Purchase This Card' on the listing detail page.",
          "The Purchase Order modal opens with card details, seller's payment path options, and the Hunt57 Purchase Terms.",
          "Check the acknowledgment box (required — you must confirm Hunt57 is not a party to this sale).",
          "Click 'Open Purchase Order'. The listing flips to Pending on all views — no one else can PO the same card while yours is active.",
          "The seller has 72 hours to VALIDATE the PO. You'll be notified when they do.",
          "Pay the seller directly using their listed method (PayPal / Cash App / Venmo). Save the receipt.",
          "Once the card is in hand and matches the listing, mark the PO COMPLETE from /transactions. The seller marks it complete too.",
          "Leave feedback (Excellent / Fair / Poor). The seller can leave feedback on you as a buyer.",
        ]} />
        <p><strong className="text-white">If anything goes wrong:</strong> do not click Complete. Message the seller first. If unresolved after 72 hours, click Dispute to bring in admin review.</p>
      </Section>

      <Section title="Fulfill a Purchase Order (Seller)" id="po-seller">
        <Steps steps={[
          "Notification bell fires when a buyer opens a PO on your card.",
          "Open /transactions and click Validate on the PO. The card is now reserved for that buyer.",
          "Wait for payment from the buyer via your listed method. When you see the funds, ship the card promptly.",
          "Mark the PO Shipped and, once the buyer confirms receipt, mark it Complete.",
          "The buyer's feedback lands on your public profile.",
        ]} />
        <p><strong className="text-white">Timing:</strong> if you don't validate within 72 hours, the PO auto-expires and the listing flips back to Active. Repeat expiries hurt your standing.</p>
      </Section>

      <Section title="Become a Vendor (Sell Cards)" id="become-vendor">
        <p>Free member accounts cannot list cards. Upgrade to vendor via one of two paths from <span className="font-mono text-[#FF5F1F]">/account</span>:</p>
        <ul className="list-disc pl-5 space-y-2 mt-2">
          <li>
            <strong className="text-white">Application path:</strong> Fill out the vendor application form (why you want to sell, experience, social handle). An admin reviews within 1-3 business days. Requires a Stripe payment method on file (30-day free trial starts on approval).
          </li>
          <li>
            <strong className="text-white">Referral code path:</strong> Get a HUNTV-XXXXXX code from a Tier 3 vendor and paste it in the Referral Fast-Track form. Instant vendor status. Also requires a Stripe card + acknowledgment of Vendor Terms.
          </li>
        </ul>
        <p>You start at Tier 1 (25 active listings max, $50 price cap). Confirmed sales + good standing auto-promote you to Tier 2 and Tier 3.</p>
      </Section>

      <Section title="Create a Listing" id="create-listing">
        <Steps steps={[
          "Go to /sell.",
          "Upload the front photo (required) and back photo (strongly recommended).",
          "Fill in card details: title auto-generates from Year + Brand + Set + Player. Manual edits allowed.",
          "Set price, shipping cost, and toggle 'Accepts Offers' (default: on). Turn OFF for FIRM listings.",
          "Enter card attributes: rookie, autograph, relic, insert, base, variation, numbered (with print run), SSP/SP, graded (company + grade + cert #).",
          "Optional notes field for condition, story, disclosures.",
          "Click Publish. Card appears in Browse immediately.",
        ]} />
        <p><strong className="text-white">Bulk upload:</strong> Sell → Bulk Upload lets you import many cards from a spreadsheet. See the template link in that flow.</p>
      </Section>

      <Section title="Manage Your Listings" id="my-listings">
        <p><span className="font-mono text-[#FF5F1F]">/my-listings</span> tabs your inventory by status:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Active</strong> — visible to buyers.</li>
          <li><strong>Pending</strong> — currently locked in a live PO.</li>
          <li><strong>Hold</strong> — paused by you (won't show in Browse). Use for pending trades, price research.</li>
          <li><strong>Sold</strong> — auto-marked when a PO completes.</li>
          <li><strong>Removed</strong> — soft-deleted.</li>
        </ul>
        <p>Bulk actions: select multiple with the checkbox, then Edit (change price, shipping, offers-toggle across all) or Delete.</p>
      </Section>

      <Section title="Combined Shipping Policy" id="combined-shipping">
        <p>Combined shipping tells buyers how to save on shipping when buying multiple cards from you.</p>
        <Steps steps={[
          "Go to /account and scroll to 'Combined Shipping'.",
          "Toggle it Enabled.",
          "Set Initial Charge (e.g. $5.99 for the first card).",
          "Set Per Additional Card (e.g. $0.25 per extra card in the same package).",
          "Set Max Cards Per Package (e.g. 20 — a new full charge starts for card 21).",
          "Save. Every current and future listing of yours shows the policy on both the browse tile (blue COMBO chip) and detail page (dedicated panel).",
        ]} />
      </Section>

      <Section title="Reputation, Strikes & Standing" id="standing">
        <p>Every user has a standing indicator (3 green checks). See the <a href="/trust-safety" className="text-[#FF5F1F] underline">Trust & Safety</a> page for the full policy, but the short version:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li><strong>Excellent (3 checks):</strong> 0 strikes, PO history clean. Default state.</li>
          <li><strong>Fair (2 checks):</strong> 1-2 strikes. Visible on your profile.</li>
          <li><strong>Poor (1 check):</strong> 3+ strikes. Listings de-prioritized, imminent suspension risk.</li>
          <li><strong>Suspended:</strong> No listings, no messaging. Contact support to appeal.</li>
        </ul>
      </Section>

      <Section title="Contact Support" id="support">
        <p>The <a href="/contact" className="text-[#FF5F1F] underline">Contact Us</a> form is strictly for <strong className="text-white">technical issues</strong> with the platform — bugs, login problems, missing features, or admin questions. It is <strong className="text-red-400">not</strong> for transaction disputes (those are between buyer and seller) or general "is this card real?" questions (post in Messages with the seller).</p>
      </Section>
    </DocPageLayout>
  );
}
